import { drawRoutineOrbit, ROUTINE_ORBIT_GAP } from '../../../../engine/core/routine-orbit.js';
/**
 * Bubble View — Canvas Renderer (mobile).
 *
 * Paints the bubble map to a single viewport-sized <canvas> instead of
 * building ~2×N DOM/SVG elements. On a 715-node map the DOM build alone cost
 * ~2s on a throttled phone; the canvas path skips DOM construction entirely
 * (render.js `canvasMode` builds physics nodes with `el: null`) and repaints
 * on demand.
 *
 * Painting is dirty-driven: markDirty() schedules a frame; the rAF loop keeps
 * itself alive only while the physics simulation is running (or a gesture
 * keeps marking dirty) and goes fully idle otherwise — no standing 60fps burn.
 *
 * Feature parity with the DOM path, mobile scope: leaf fills + parent rings,
 * leaf/parent name labels, selection ring, done-status strikethrough, tag
 * spotlight dimming, collapsed "+" hint, tap-to-select hit testing. Desktop
 * affordances (drag-reparent, hover previews, badges, curved labels) remain
 * DOM-only — desktop keeps the DOM renderer.
 */

import { safeColor } from "../../../../engine/core/validation.js";
import { t } from "../../../../shared/i18n/index.js";
import { wrapLines as wrapLinesWith, fitText as fitTextWith, contrastColor } from "./canvas-text.js";

let canvasEl = null;
let ctx = null;
let host = null;
let getPhysicsNodesFn = null;
let getViewTransformFn = null;
let onTapFn = null;
let onLongPressFn = null;
let getSelectedIdFn = null;
let isPhysicsRunningFn = null;
let rafId = null;
let resizeObserver = null;
let themeObserver = null;
let dirty = false;
let wasPhysicsActive = false;
let themeText = "#dddddd";
let themeOrbit = "#8b949e";
let themeMuted = "rgba(221,221,221,0.7)";
// Selection uses the app-wide accent (sidebar tree outline, outline row
// highlight all use --accent-cyan) so selection reads the same everywhere.
let themeAccent = "#00d4aa";
// One layout per live physics node; panning and physics move bubbles without
// changing their text metrics. Weak keys release old documents / rebuilt nodes.
let labelLayouts = new WeakMap();

const DPR_CAP = 1.5;

/**
 * Mount the canvas overlay inside the bubble viewport.
 *
 * @param {Object} opts
 * @param {HTMLElement} opts.host          Bubble viewport element (screen space).
 * @param {Function} opts.getPhysicsNodes  Returns the current physics-node array.
 * @param {Function} opts.getViewTransform Returns {zoom, panX, panY} of the world.
 * @param {Function} opts.getSelectedId    Returns the currently selected node id.
 * @param {Function} opts.isPhysicsRunning Returns whether the simulation is live.
 * @param {Function} opts.onTap            (nodeId) => void on canvas tap hit.
 * @param {Function} [opts.onLongPress]    (nodeId) => void on ~500ms hold.
 */
export function mount(opts) {
  if (canvasEl) return;
  host = opts.host;
  getPhysicsNodesFn = opts.getPhysicsNodes;
  getViewTransformFn = opts.getViewTransform;
  getSelectedIdFn = opts.getSelectedId || (() => null);
  isPhysicsRunningFn = opts.isPhysicsRunning || (() => false);
  onTapFn = opts.onTap;
  onLongPressFn = opts.onLongPress || null;

  canvasEl = document.createElement("canvas");
  canvasEl.className = "bubble-canvas-overlay";
  canvasEl.style.cssText = [
    "position: absolute",
    "left: 0",
    "top: 0",
    "width: 100%",
    "height: 100%",
    "pointer-events: auto",
    "touch-action: none",
    "z-index: 2",
  ].join(";");
  host.appendChild(canvasEl);
  ctx = canvasEl.getContext("2d");

  // Canvas colors must be resolved again when the theme changes.
  updateTheme();
  themeObserver = new MutationObserver(() => { updateTheme(); markDirty(); });
  themeObserver.observe(document.documentElement, {attributes: true, attributeFilter: ['data-theme']});

  resize();
  if (typeof ResizeObserver !== "undefined") {
    resizeObserver = new ResizeObserver(() => { resize(); markDirty(); });
    resizeObserver.observe(host);
  } else {
    window.addEventListener("resize", onWindowResize);
  }

  canvasEl.addEventListener("pointerdown", onPointerDown);
  canvasEl.addEventListener("pointermove", onPointerMove);
  canvasEl.addEventListener("pointerup", onPointerUp);
  canvasEl.addEventListener("pointercancel", onPointerCancel);
  // Long-press is ours; keep the browser's context menu / text callout out.
  canvasEl.addEventListener("contextmenu", onContextMenu);

  markDirty();
}

function updateTheme() {
  const style = getComputedStyle(host);
  themeText = style.getPropertyValue('--text-primary').trim() || '#dddddd';
  themeOrbit = style.getPropertyValue('--text-secondary').trim() || '#8b949e';
  themeAccent = style.getPropertyValue('--accent-cyan').trim() || '#00d4aa';
  themeMuted = style.getPropertyValue('--text-muted').trim() || themeText;
}

function onWindowResize() {
  resize();
  markDirty();
}

export function unmount() {
  themeObserver?.disconnect();
  themeObserver = null;
  labelLayouts = new WeakMap();
  if (rafId) cancelAnimationFrame(rafId);
  rafId = null;
  dirty = false;
  wasPhysicsActive = false;
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  } else {
    window.removeEventListener("resize", onWindowResize);
  }
  if (canvasEl) {
    canvasEl.removeEventListener("pointerdown", onPointerDown);
    canvasEl.removeEventListener("pointermove", onPointerMove);
    canvasEl.removeEventListener("pointerup", onPointerUp);
    canvasEl.removeEventListener("pointercancel", onPointerCancel);
    canvasEl.removeEventListener("contextmenu", onContextMenu);
    canvasEl.remove();
    canvasEl = null;
    ctx = null;
  }
  clearTimeout(longPressTimer);
  longPressTimer = null;
  host = null;
  getPhysicsNodesFn = null;
  getViewTransformFn = null;
  onTapFn = null;
  onLongPressFn = null;
  isPhysicsRunningFn = null;
}

export function isMounted() {
  return !!canvasEl;
}

/**
 * Request a repaint. Cheap and idempotent — call on any change that affects
 * what's on screen (data, selection, transform, physics settle).
 */
export function markDirty() {
  if (!canvasEl) return;
  dirty = true;
  ensureLoop();
}

// ============================================================================
// INTERNALS
// ============================================================================

function ensureLoop() {
  if (rafId) return;
  rafId = requestAnimationFrame(tick);
}

function tick() {
  rafId = null;
  if (!canvasEl) return;
  const physActive = !!isPhysicsRunningFn?.();
  if (physActive || dirty || wasPhysicsActive) {
    dirty = false;
    drawFrame();
  }
  // Keep animating while the simulation runs; paint one settling frame after
  // it stops (wasPhysicsActive) so the final positions always land on screen.
  wasPhysicsActive = physActive;
  if (physActive) ensureLoop();
}

function resize() {
  if (!canvasEl || !host) return;
  const rect = host.getBoundingClientRect();
  const dpr = Math.min(window.devicePixelRatio || 1, DPR_CAP);
  const w = Math.max(1, Math.round(rect.width * dpr));
  const h = Math.max(1, Math.round(rect.height * dpr));
  if (canvasEl.width !== w) canvasEl.width = w;
  if (canvasEl.height !== h) canvasEl.height = h;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
}

function drawFrame() {
  if (!ctx || !canvasEl || !getPhysicsNodesFn) return;
  const rect = host.getBoundingClientRect();
  const cssW = rect.width;
  const cssH = rect.height;

  ctx.clearRect(0, 0, cssW, cssH);

  const tx = getViewTransformFn?.() || { zoom: 1, panX: 0, panY: 0 };
  const z = tx.zoom || 1;
  const px = tx.panX || 0;
  const py = tx.panY || 0;

  const nodes = getPhysicsNodesFn() || [];
  if (!nodes.length) {
    // The DOM empty-state lives in the hidden world layer — paint the hint
    // here so an empty document isn't just a blank canvas.
    ctx.font = "600 15px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = themeMuted;
    ctx.fillText(t("views.noItems"), cssW / 2, cssH / 2);
    return;
  }
  const selectedId = getSelectedIdFn?.();

  // Parents first (rings), leaves on top (filled) — painter order matches the
  // DOM's stacking. Absolute coords are accumulated top-down in one pass
  // (children follow parents in the physics array by construction).
  const abs = new Map(); // id -> {x, y} absolute center in world coords

  const parents = [];
  const leaves = [];
  for (const n of nodes) {
    let ax = n.offsetX + n.x;
    let ay = n.offsetY + n.y;
    if (n.parent) {
      const pa = abs.get(n.parent.id);
      if (pa) {
        ax += pa.x - n.parent.radius;
        ay += pa.y - n.parent.radius;
      }
    }
    abs.set(n.id, { x: ax, y: ay });
    (n.isLeaf ? leaves : parents).push(n);
  }

  for (const n of parents) paintBubble(n, abs, cssW, cssH, px, py, z, false, n.id === selectedId);
  for (const n of leaves) paintBubble(n, abs, cssW, cssH, px, py, z, true, n.id === selectedId);
}

function paintBubble(n, abs, cssW, cssH, panX, panY, zoom, isLeaf, isSelected) {
  const a = abs.get(n.id);
  const sx = panX + a.x * zoom;
  const sy = panY + a.y * zoom;
  const outerRadius = n.radius * zoom;
  const sr = (n.bodyRadius ?? n.radius) * zoom;

  // Viewport cull (labels overhang a little, hence the margin).
  if (sx + outerRadius < -40 || sy + outerRadius < -40) return;
  if (sx - outerRadius > cssW + 40 || sy - outerRadius > cssH + 40) return;
  // Under a pixel's radius on screen, not drawn (Alex, 2026-10-15: "stop
  // drawing a node when it is 1px only"): it was enlarged to a 1 px dot, and
  // a 20,000-item map seen whole is mostly such dots. Its children are
  // smaller still.
  if (sr < 1) return;

  const color = safeColor(n.color ?? n.dataNode?.color, "#888");
  const dimmed = n.spotlight === "dim";
  const isDone = n.status === "done";

  ctx.globalAlpha = dimmed ? 0.18 : 1;

  ctx.beginPath();
  ctx.arc(sx, sy, sr, 0, Math.PI * 2);
  if (isLeaf) {
    ctx.fillStyle = color;
    ctx.fill();
    ctx.lineWidth = isSelected ? 3 : 1;
    ctx.strokeStyle = isSelected ? themeAccent : "rgba(0,0,0,0.25)";
    ctx.stroke();
  } else {
    ctx.lineWidth = isSelected ? 3 : 2;
    ctx.strokeStyle = isSelected ? themeAccent : color;
    ctx.stroke();
  }

  if (n.routine) {
    const bodyRadius = n.bodyRadius ?? n.radius;
    drawRoutineOrbit(ctx, sx, sy, bodyRadius * 2, bodyRadius * 2, bodyRadius, themeOrbit, zoom);
  }

  // Labels — skipped when too small on screen to be legible.
  const name = n.name;
  if (name) {
    if (isLeaf && sr >= 8) {
      const fontSize = Math.max(8, Math.min(15, sr * 0.34));
      ctx.font = `500 ${fontSize}px system-ui, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = contrastColor(color);
      // Wrap into up to 3 lines inside the circle — a single truncated
      // line wasted most of the bubble's width on longer titles.
      const maxW = sr * 1.7;
      const lineH = fontSize * 1.15;
      const maxLines = Math.max(1, Math.min(3, Math.floor((sr * 1.4) / lineH)));
      const layout = labelLayout(n, maxW, maxLines, true);
      const lines = layout.lines;
      const spread = (lines.length - 1) * lineH;
      lines.forEach((line, i) => {
        const y = sy - spread / 2 + i * lineH;
        ctx.fillText(line, sx, y);
        if (isDone) strikeThrough(layout.widths[i] ??= ctx.measureText(line).width, sx, y, ctx.fillStyle);
      });
    } else if (!isLeaf && sr >= 20) {
      const fontSize = Math.max(10, Math.min(15, sr * 0.16));
      ctx.font = `600 ${fontSize}px system-ui, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "bottom";
      ctx.fillStyle = themeText;
      const layout = labelLayout(n, Math.max(70, sr * 1.6), 1, false);
      const label = layout.lines[0];
      const ly = sy - (n.routine ? sr + ROUTINE_ORBIT_GAP * zoom : outerRadius) - 3;
      ctx.fillText(label, sx, ly);
      if (isDone) strikeThrough(layout.widths[0] ??= ctx.measureText(label).width, sx, ly - fontSize / 2 + 1, themeText);
    }
  }

  // Collapsed-parent hint: matches the DOM's "+" toggle badge.
  if (n.collapsedParent && sr >= 10) {
    const fs = Math.max(9, Math.min(14, sr * 0.4));
    ctx.font = `700 ${fs}px system-ui, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = isLeaf ? contrastColor(color) : themeMuted;
    ctx.fillText("+", sx, sy + sr * 0.55);
  }

  ctx.globalAlpha = 1;
}

function labelLayout(node, width, maxLines, wrap) {
  const font = ctx.font;
  const cached = labelLayouts.get(node);
  if (cached && cached.text === node.name && cached.font === font
      && cached.width === width && cached.maxLines === maxLines && cached.wrap === wrap) return cached;
  const layout = {
    text: node.name, font, width, maxLines, wrap,
    lines: wrap ? wrapLines(node.name, width, maxLines) : [fitText(node.name, width)],
    widths: [],
  };
  labelLayouts.set(node, layout);
  return layout;
}

function strikeThrough(w, cx, cy, color) {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(cx - w / 2, cy);
  ctx.lineTo(cx + w / 2, cy);
  ctx.stroke();
  ctx.restore();
}

// Text helpers (canvas-text.js), measured with this canvas's context.
const wrapLines = (text, maxWidth, maxLines) => wrapLinesWith(ctx, text, maxWidth, maxLines);
const fitText = (text, maxWidth) => fitTextWith(ctx, text, maxWidth);

// ============================================================================
// HIT TESTING / TAP
// ============================================================================

let downX = 0;
let downY = 0;
let downTime = 0;
let longPressTimer = null;
let longPressFired = false;

const LONG_PRESS_MS = 500;
const LONG_PRESS_SLOP_PX = 10;

/**
 * After a long-press the browser still synthesizes a click when the finger
 * lifts — by then the node panel covers the canvas, so that ghost click
 * would land on whatever panel control renders under the finger. Swallow
 * the first click arriving within a short window after the lift.
 */
function suppressPostLongPressClick() {
  const arm = () => {
    const swallow = (ev) => {
      ev.stopPropagation();
      ev.preventDefault();
      window.removeEventListener("click", swallow, true);
    };
    window.addEventListener("click", swallow, true);
    setTimeout(() => window.removeEventListener("click", swallow, true), 150);
  };
  window.addEventListener("pointerup", arm, { capture: true, once: true });
  setTimeout(() => window.removeEventListener("pointerup", arm, true), 3000);
}

function onPointerDown(e) {
  clearTimeout(longPressTimer);
  longPressTimer = null;
  // Second finger = pinch, not a press.
  if (!e.isPrimary) return;
  downX = e.clientX;
  downY = e.clientY;
  downTime = Date.now();
  longPressFired = false;
  if (!onLongPressFn) return;
  longPressTimer = setTimeout(() => {
    longPressTimer = null;
    if (!canvasEl) return;
    const rect = canvasEl.getBoundingClientRect();
    const id = hitTest(downX - rect.left, downY - rect.top);
    if (id) {
      longPressFired = true;
      try { navigator.vibrate?.(15); } catch { /* unsupported */ }
      suppressPostLongPressClick();
      onLongPressFn(id);
    }
  }, LONG_PRESS_MS);
}

function onPointerMove(e) {
  if (!longPressTimer || !e.isPrimary) return;
  if (Math.hypot(e.clientX - downX, e.clientY - downY) > LONG_PRESS_SLOP_PX) {
    clearTimeout(longPressTimer);
    longPressTimer = null;
  }
}

function onPointerCancel() {
  clearTimeout(longPressTimer);
  longPressTimer = null;
}

function onContextMenu(e) {
  e.preventDefault();
}

function onPointerUp(e) {
  clearTimeout(longPressTimer);
  longPressTimer = null;
  if (longPressFired) {
    // The hold already opened the node properties — don't also select-tap.
    longPressFired = false;
    return;
  }
  if (!canvasEl || !onTapFn) return;
  // Tap, not drag: selecting on pointerDOWN would change the selection every
  // time a pan gesture happens to start on a bubble (the DOM path never had
  // this — browsers suppress click after a drag). 8px slop, 600ms budget.
  const moved = Math.hypot(e.clientX - downX, e.clientY - downY);
  if (moved > 8 || Date.now() - downTime > 600) return;

  const rect = canvasEl.getBoundingClientRect();
  const px = e.clientX - rect.left;
  const py = e.clientY - rect.top;

  const id = hitTest(px, py);
  // Tap on empty space clears the selection (null) — matches the outline's
  // tap-to-deselect and lets the + button target the root again.
  onTapFn(id || null);
  markDirty(); // selection ring
}

/**
 * Find the topmost (smallest, deepest) node under the given screen point.
 */
function hitTest(px, py) {
  if (!getPhysicsNodesFn || !getViewTransformFn) return null;
  const tx = getViewTransformFn();
  const z = tx.zoom || 1;
  const panX = tx.panX || 0;
  const panY = tx.panY || 0;

  const nodes = getPhysicsNodesFn() || [];
  const abs = new Map();
  let bestId = null;
  let bestRadius = Infinity;

  for (const n of nodes) {
    let ax = n.offsetX + n.x;
    let ay = n.offsetY + n.y;
    if (n.parent) {
      const pa = abs.get(n.parent.id);
      if (pa) {
        ax += pa.x - n.parent.radius;
        ay += pa.y - n.parent.radius;
      }
    }
    abs.set(n.id, { x: ax, y: ay });
    const sx = panX + ax * z;
    const sy = panY + ay * z;
    const sr = n.radius * z;
    const dx = px - sx;
    const dy = py - sy;
    if (dx * dx + dy * dy <= sr * sr && sr < bestRadius) {
      bestRadius = sr;
      bestId = n.id;
    }
  }
  return bestId;
}
